import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { useGetUser } from "../../api/queries/useGetUser";
import { useAppContext } from "../../Context/AppContext";
import StepsProgressBar from "../Registration/StepsProgressBar";
import MembershipStep from "../Registration/RegistrationsSteps/MemberShipStep";
import TermsStep from "../Registration/RegistrationsSteps/TermsStep";
import CheckoutStep from "../Registration/RegistrationsSteps/CheckoutStep";
import "../css/RegisterForm.css";

interface FormData {
  membershipName: string;
  isAnnualMembership: number;
  isTermConditions: number;
  isDirectory: number;
  discountCode?: string;
}

const RenewMembership: React.FC = () => {
  const navigate = useNavigate();
  const { objectId } = useAppContext();
  const { data: memberships } = useGetMemberships();
  const { data: userDataResponse } = useGetUser({ id: objectId || "", enabled: !!objectId });

  const [step, setStep] = useState(0);
  const [membershipPrice, setMembershipPrice] = useState(0);
  const [discountCode, setDiscountCode] = useState("");
  const [formData, setFormData] = useState<FormData>({
    membershipName: "Business Membership",
    // Only monthly memberships are active now
    isAnnualMembership: 0,
    isTermConditions: 0,
    isDirectory: 0,
  });

  const stepLabels = ["Choose membership", "Terms & conditions", "Payment", "Confirmation"];

  const user = userDataResponse?.user;
  const userDataForCheckout = user
    ? {
        ...user,
        objectId: objectId || "",
        membershipName: formData.membershipName,
        isAnnualMembership: formData.isAnnualMembership,
        discountCode,
      }
    : null;

  useEffect(() => {
    if (!user) return;
    // Keep the user's membership type as default, but always use monthly billing
    setFormData((prev) => ({
      ...prev,
      membershipName: user.membershipName || prev.membershipName,
      isAnnualMembership: 0,
    }));
  }, [user]);

  useEffect(() => {
    if (step === 2 && userDataForCheckout) {
      localStorage.setItem("UserData", JSON.stringify(userDataForCheckout));
    }
  }, [step, userDataForCheckout]);

  const nextStep = () => setStep((s) => s + 1);
  const prevStep = () => setStep((s) => Math.max(s - 1, 0));

  const handleMembershipNext = (data: Partial<FormData>, price: number) => {
    setFormData((prev) => ({ ...prev, ...data }));
    setMembershipPrice(price);
    nextStep();
  };

  const handleTermsNext = (data: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...data }));
    nextStep();
  };

  const handleCheckoutNext = () => {
    nextStep();
  };

  if (!user) {
    return null;
  }

  const membershipsList = Array.isArray(memberships) ? memberships : memberships ? [memberships] : [];

  // Disable current plan only when user has ACTIVE membership (upgrade/downgrade). When renewing after cancel, allow selecting same plan.
  const isMembershipActive =
    user.status !== "InActive" &&
    user.stripeStatus?.toLowerCase() !== "canceled" &&
    user.stripeStatus?.toLowerCase() !== "cancelled";
  const currentMembershipToDisable = isMembershipActive ? (user.membershipName ?? undefined) : undefined;

  return (
    <div className="m-4 p-4 rounded-4 border-0 shadow d-flex flex-column">
      <button
        type="button"
        className="btn btn-link p-0 mb-2 text-decoration-none align-self-start"
        onClick={() => navigate("/manage-membership")}
      >
        ← Back to Manage Membership
      </button>
      <h4 className="mb-3">Renew / Upgrade membership</h4>
      <StepsProgressBar currentStep={step} labels={stepLabels} />

      {step === 0 && (
        <MembershipStep
          discountCode={discountCode}
          setDiscountCode={setDiscountCode}
          defaultValues={{
            membershipName: formData.membershipName,
            isAnnualMembership: formData.isAnnualMembership,
          }}
          memberships={membershipsList}
          setMembershipPrice={setMembershipPrice}
          onNext={handleMembershipNext}
          onBack={() => navigate("/manage-membership")}
          currentMembershipName={currentMembershipToDisable}
        />
      )}

      {step === 1 && (
        <TermsStep
          defaultValues={{
            isTermConditions: formData.isTermConditions,
            isDirectory: formData.isDirectory,
          }}
          onNext={handleTermsNext}
          onBack={prevStep}
        />
      )}

      {step === 2 && (
        <CheckoutStep
          selectedMembership={formData.membershipName}
          membershipPrice={membershipPrice}
          memberships={membershipsList}
          isAnnualMembership={formData.isAnnualMembership === 1 ? "annual" : "monthly"}
          onNext={handleCheckoutNext}
          onBack={prevStep}
          discountCode={discountCode}
        />
      )}

      {step === 3 && (
        <div className="p-4 text-center">
          <h5 className="text-success mb-3">Membership renewed successfully</h5>
          <p className="text-muted mb-4">
            Your membership has been updated. You can now access all member benefits.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => navigate("/dashboard")}
          >
            Go to Dashboard
          </button>
        </div>
      )}
    </div>
  );
};

export default RenewMembership;
